import React, { useState, useMemo } from 'react';
import managecookies from '../services/managecookies';
import Icon from '../comps/Icon';
import '../style/css/share2026.css';

/*
 * ShareBooking (שיתוף הזמנות) — operator tool to share the business's public
 * booking page and invite a customer to book by entering their phone.
 * Operator intent: "שיתוף לינק לקביעת מקום באמצעות הזנת מספר טלפון".
 *
 * All client-side / demo — no real SMS is sent. The public link points at the
 * real /b/:slug page. Future: an act to send a tokenized booking-invite SMS.
 */

// The current business's public slug. Prefer the REAL slug the server assigned
// (business.slug — set on the login response); only fall back to deriving one from
// the name if the session predates the slug field (older login payload).
function useBusinessSlug() {
    return useMemo(() => {
        try {
            const data = JSON.parse(managecookies.getCookieVal('userData') || '{}');
            const biz = data.business && data.business[0];
            // Real server slug wins — it's the canonical /b/:slug the public page resolves.
            const realSlug = biz && typeof biz.slug === 'string' && biz.slug.trim();
            if (realSlug) return realSlug;
            const name = biz && (biz.name || (biz.names && biz.names[0] && biz.names[0].name));
            if (name) {
                // Strict slug: keep only word chars + Hebrew letters, collapse the rest to
                // hyphens, trim edges — so names with / ? # % quotes can't break the URL.
                const s = String(name).trim().toLowerCase()
                    .replace(/[^\w֐-׿]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                return s || 'my-restaurant';
            }
        } catch (e) { /* ignore */ }
        return 'my-restaurant';
    }, []);
}

export default function ShareBooking() {
    const slug = useBusinessSlug();
    const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/b/${encodeURIComponent(slug)}`;

    const [phone, setPhone] = useState('');
    const [copied, setCopied] = useState(false);
    const [toast, setToast] = useState('');

    React.useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(''), 2800);
        return () => clearTimeout(t);
    }, [toast]);

    const copyLink = async () => {
        if (!(navigator.clipboard && navigator.clipboard.writeText)) {
            setToast('העתקה אוטומטית לא זמינה — סמנו והעתיקו ידנית.');
            return;
        }
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (e) {
            setToast('העתקה אוטומטית לא זמינה — סמנו והעתיקו ידנית.');
        }
    };

    const validPhone = phone.replace(/\D/g, '').length >= 9;

    const sendInvite = (e) => {
        e.preventDefault();
        if (!validPhone) return;
        setToast(`קישור הזמנה ישלח ל-${phone} ב-SMS (הדגמה — לא נשלח SMS אמיתי).`);
        setPhone('');
    };

    return (
        <div className="sh-page">
            <div className="sh-demo-note"><Icon name="info" size={15} style={{ verticalAlign: 'middle' }} /> תצוגה מקדימה · שליחת ה-SMS היא הדגמה — הקישור עצמו אמיתי</div>

            <header className="sh-header">
                <h1 className="sh-title">שיתוף הזמנות</h1>
                <p className="sh-subtitle">שתפו את עמוד ההזמנות של העסק, או הזמינו לקוח לקבוע מקום ישירות.</p>
            </header>

            <div className="sh-grid">
                {/* Public link + copy + QR placeholder */}
                <section className="sh-card">
                    <h2 className="sh-card-title">קישור ההזמנה הציבורי</h2>
                    <p className="sh-card-desc">כל מי שיפתח את הקישור יוכל להזמין מקום בעסק שלכם.</p>

                    <div className="sh-link-row">
                        <input className="sh-link-input" type="text" readOnly value={link} dir="ltr"
                            onFocus={(e) => e.target.select()} aria-label="קישור ההזמנה" />
                        <button type="button" className="sh-copy-btn" onClick={copyLink}>
                            {copied ? '✓ הועתק' : 'העתקה'}
                        </button>
                    </div>

                    <div className="sh-share-actions">
                        <a className="sh-share-btn" href={`https://wa.me/?text=${encodeURIComponent('להזמנת מקום: ' + link)}`}
                            target="_blank" rel="noopener noreferrer">שיתוף ב-WhatsApp</a>
                        <a className="sh-share-btn" href={`mailto:?subject=${encodeURIComponent('הזמנת מקום')}&body=${encodeURIComponent(link)}`}>
                            שיתוף במייל
                        </a>
                    </div>

                    <div className="sh-qr">
                        <div className="sh-qr-box">
                            <span className="sh-qr-emoji" aria-hidden="true"><Icon name="qr" size={40} /></span>
                            <span className="sh-qr-cap">מקום לקוד QR</span>
                        </div>
                        <p className="sh-qr-note">כאן יופיע קוד QR סריק בהמשך — להדפסה לשולחנות.</p>
                    </div>
                </section>

                {/* Phone invite */}
                <section className="sh-card">
                    <h2 className="sh-card-title">הזמנת לקוח לפי טלפון</h2>
                    <p className="sh-card-desc">הזינו מספר טלפון — במערכת החיה יישלח ללקוח קישור אישי לקביעת מקום ב-SMS. (בתצוגה זו לא נשלח SMS.)</p>

                    <form className="sh-invite" onSubmit={sendInvite}>
                        <label className="sh-label" htmlFor="sh-phone">מספר טלפון</label>
                        <input id="sh-phone" className="sh-input" type="tel" dir="ltr"
                            placeholder="050-0000000" value={phone}
                            onChange={(e) => setPhone(e.target.value)} />
                        <button type="submit" className="sh-invite-btn" disabled={!validPhone}>
                            שליחת קישור ב-SMS
                        </button>
                    </form>

                    <ul className="sh-steps">
                        <li><span className="sh-step-n">1</span> במערכת החיה הלקוח מקבל SMS עם קישור אישי.</li>
                        <li><span className="sh-step-n">2</span> בוחר תאריך, שעה ומספר סועדים.</li>
                        <li><span className="sh-step-n">3</span> ההזמנה נכנסת ישירות ליומן וללוח ה-LIVE.</li>
                    </ul>
                </section>
            </div>

            {toast && <div className="sh-toast" role="status">{toast}</div>}
        </div>
    );
}
